// 🚀 PAYMENT SOCKET SERVICE - ANGULAR
// Propósito: Manejar eventos de socket específicos para pagos asíncronos

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SocketioService } from './socketio.service';

// Importar SweetAlert2 para mostrar alertas
declare var Swal: any;

export interface PaymentStatusChange {
  paymentId: string;
  ownerId: string;
  status: 'COMPLETED' | 'FAILED';
  verification_type: 'p2c' | 'zelle' | 'transferencia';
  amount: number;
  timestamp: Date;
}

export interface OwnerPaymentsUpdate {
  ownerId: string;
  action: 'payment_verified' | 'payment_failed';
  paymentCount: number;
  timestamp: Date;
}

@Injectable({
  providedIn: 'root'
})
export class PaymentSocketService {

  // 📡 Observables para eventos de pago
  private paymentStatusChanged$ = new BehaviorSubject<PaymentStatusChange | null>(null);
  private ownerPaymentsUpdated$ = new BehaviorSubject<OwnerPaymentsUpdate | null>(null);
  private specificPaymentUpdate$ = new BehaviorSubject<any>(null);
  private specificOwnerUpdate$ = new BehaviorSubject<any>(null);

  constructor(private socketService: SocketioService) {
    this.setupPaymentSocketListeners();
  }

  /**
   * 🎧 CONFIGURAR LISTENERS DE SOCKET PARA PAGOS
   */
  private setupPaymentSocketListeners(): void {
    // Solo configurar si el socket está disponible
    if (this.socketService.socketConnet) {
      this.initializeListeners();
    } else {
      // Esperar a que el socket se conecte
      setTimeout(() => {
        if (this.socketService.socketConnet) {
          this.initializeListeners();
        }
      }, 1000);
    }
  }

  /**
   * 🔌 INICIALIZAR TODOS LOS LISTENERS
   */
  private initializeListeners(): void {
    const socket = this.socketService.socketConnet;

    // 📡 Listener para cambios de estado de pago
    socket.on('payment-status-changed', (data: PaymentStatusChange) => {
      console.log('🔔 [PAYMENT-SOCKET] payment-status-changed:', data);
      this.paymentStatusChanged$.next(data);
    });

    // 📊 Listener para actualizaciones de pagos del owner
    socket.on('owner-payments-updated', (data: OwnerPaymentsUpdate) => {
      console.log('🔔 [PAYMENT-SOCKET] owner-payments-updated:', data);
      this.ownerPaymentsUpdated$.next(data);
    });

    // 🎯 Listener dinámico para actualizaciones específicas de pago
    // Nota: Este se configurará dinámicamente según el ownerId
  }

  /**
   * 🎯 ESCUCHAR ACTUALIZACIONES ESPECÍFICAS DE UN OWNER
   * @param ownerId ID del propietario (person)
   */
  listenToOwnerUpdates(ownerId: string): void {
    const socket = this.socketService.socketConnet;
    if (socket) {
      const eventName = `owner-payments-${ownerId}`;
      
      socket.on(eventName, (data: any) => {
        console.log(`🎯 [PAYMENT-SOCKET] ${eventName}:`, data);
        this.specificOwnerUpdate$.next({
          ...data,
          ownerId: ownerId
        });
      });

      console.log(`🎧 [PAYMENT-SOCKET] Escuchando evento: ${eventName}`);
    }
  }

  /**
   * 🔍 ESCUCHAR ACTUALIZACIONES ESPECÍFICAS DE UN PAGO
   * @param ownerId ID del propietario para filtrar eventos
   */
  listenToPaymentUpdates(ownerId: string): void {
    const socket = this.socketService.socketConnet;
    if (socket) {
      const eventName = `payment-update-${ownerId}`;
      
      socket.on(eventName, (data: any) => {
        console.log(`🔍 [PAYMENT-SOCKET] ${eventName}:`, data);
        this.specificPaymentUpdate$.next({
          ...data,
          ownerId: ownerId
        });
      });

      console.log(`🎧 [PAYMENT-SOCKET] Escuchando evento: ${eventName}`);
    }
  }

  /**
   * 🧹 LIMPIAR LISTENERS ESPECÍFICOS
   * @param ownerId ID del propietario
   */
  cleanupOwnerListeners(ownerId: string): void {
    const socket = this.socketService.socketConnet;
    if (socket) {
      socket.off(`owner-payments-${ownerId}`);
      socket.off(`payment-update-${ownerId}`);
      console.log(`🧹 [PAYMENT-SOCKET] Limpiados listeners para owner: ${ownerId}`);
    }
  }

  // 📤 GETTERS PARA OBSERVABLES
  get paymentStatusChanges(): Observable<PaymentStatusChange | null> {
    return this.paymentStatusChanged$.asObservable();
  }

  get ownerPaymentsUpdates(): Observable<OwnerPaymentsUpdate | null> {
    return this.ownerPaymentsUpdated$.asObservable();
  }

  get specificPaymentUpdates(): Observable<any> {
    return this.specificPaymentUpdate$.asObservable();
  }

  get specificOwnerUpdates(): Observable<any> {
    return this.specificOwnerUpdate$.asObservable();
  }

  /**
   * 🔄 REFRESCAR CONFIGURACIÓN DE LISTENERS
   * Útil cuando se reconecta el socket
   */
  refreshListeners(): void {
    console.log('🔄 [PAYMENT-SOCKET] Refrescando listeners de pago...');
    this.setupPaymentSocketListeners();
  }


}
